import { getRoleLabel } from 'modules/teams/domain/constants/role-labels';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { TeamParticipant } from 'modules/teams/domain/interfaces/team-participant';
import { changePlayerRole, deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import React, { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { msg, ph } from '@lingui/core/macro';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Role } from 'shared/types/role';
import { getFullName } from 'shared/utils';
import { AsyncState } from 'shared/utils/async-state';

import { AppDispatch } from 'store/store';
import { UDEntityList } from 'ud-ui/components';
import ParticipantRow from './participant-row';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

interface Props {
  teamId: number;
  participants?: TeamParticipant[];
}

const TeamsComponentsFormParticipantsListPart: React.FC<Props> = ({ teamId, participants = [] }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const [filterValue, setFilterValue] = useState('');
  const [loadingParticipantId, setLoadingParticipantId] = useState<number | null>(null);

  // Обёртка `try/catch` вокруг `dispatch` была мёртвой: синхронная часть не бросает, а отказ
  // thunk-а ловит `.catch` промиса.
  const changeRole = useCallback(
    (participant: TeamParticipant, role: string) => {
      const number = participant.number ?? 0;
      const position = participant.position ?? PlayerPosition.MIDFIELDER;

      setLoadingParticipantId(participant.id);

      dispatch(
        changePlayerRole({
          teamId,
          userId: participant.id,
          role: role as Role,
          number,
          position: position?.toLowerCase() as PlayerPosition,
        }),
      )
        .unwrap()
        .then(() => {
          const roleLabel = getRoleLabel(role);
          const roleName = roleLabel ? t(roleLabel) : role;
          toast.success(t`Роль участника ${ph({ participant: getFullName(participant) })} изменена на ${ph({ role: roleName })}`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось изменить роль участника ${ph({ participant: getFullName(participant) })}`);
        })
        .finally(() => {
          setLoadingParticipantId(null);
        });
    },
    [dispatch, t, teamId, toastApiError],
  );

  // Промис — кнопке «Исключить»: она заперта до ответа сама; `loadingParticipantId` гасит селект роли строки.
  const kickFromTeam = useCallback(
    (participant: TeamParticipant) => {
      setLoadingParticipantId(participant.id);

      return dispatch(deleteTournamentPlayerApplication({ teamId, userId: participant.id }))
        .unwrap()
        .then(() => {
          toast.success(t`Участник ${ph({ participant: getFullName(participant) })} исключен из команды`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось исключить ${ph({ participant: getFullName(participant) })} из команды`);
        })
        .finally(() => {
          setLoadingParticipantId(null);
        });
    },
    [dispatch, t, teamId, toastApiError],
  );

  const participantsByFilter = useMemo(() => {
    return participants.filter((participant) => {
      return getFullName(participant).toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [participants, filterValue]);

  // Вкладка получает готовый массив и сети не видит: ветки загрузки и ошибки рисует
  // карточка команды выше по дереву (`team-edit-page.tsx`). Считать надо по
  // отфильтрованному списку, иначе ветка «не найдено» никогда не покажется.
  const state: AsyncState = useMemo(
    () => ({ status: participantsByFilter.length > 0 ? 'ready' : 'empty', isRefreshing: false }),
    [participantsByFilter.length],
  );

  const renderParticipant = useCallback(
    (participant: TeamParticipant, index: number) => (
      <ParticipantRow
        participant={participant}
        index={index + 1}
        isLoading={loadingParticipantId === participant.id}
        onChangeRole={changeRole}
        onKick={kickFromTeam}
      />
    ),
    [changeRole, kickFromTeam, loadingParticipantId],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <UDEntityList<TeamParticipant>
        items={participantsByFilter}
        renderItem={renderParticipant}
        getItemKey={(participant) => participant.id}
        state={state}
        search={filterValue}
        onSearchChange={setFilterValue}
        searchPlaceholder={t`ФИО Участника`}
        emptyText={t`В команде пока нет участников`}
        notFoundText={t`Участники не найдены`}
      />
    </div>
  );
};

export default TeamsComponentsFormParticipantsListPart;
